// ============================================================================
//  AttendanceRulesPage.tsx — Gestion des règles de pointage (horaires)
//  Sous RH · gated attendance:edit. Bureau, Restaurant, équipe du soir…
//  Chaque règle sert de base au calcul des retards/heures (biométrie & web).
// ============================================================================

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Clock, Plus, Pencil, Trash2, Star, X, Save, Loader2 } from 'lucide-react'
import { hrApi, type AttendanceRule } from '@/modules/hr/api/hr.api'
import { usePermission } from '@/modules/auth/hooks/usePermission'
import { useNotificationStore } from '@/stores/notification.store'

const Spinner = () => <div className="w-5 h-5 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" />

type RuleForm = {
  name: string
  workStartTime: string
  workEndTime: string
  lateThreshold: number
  lunchBreakMinutes: number
  weeklyHours: number
  overtimeThreshold: number
  debounceMinutes: number
  isDefault: boolean
}

const EMPTY: RuleForm = {
  name: '', workStartTime: '08:00', workEndTime: '17:00',
  lateThreshold: 15, lunchBreakMinutes: 60, weeklyHours: 40,
  overtimeThreshold: 8, debounceMinutes: 5, isDefault: false,
}

export default function AttendanceRulesPage() {
  const qc = useQueryClient()
  const { can } = usePermission()
  const { addNotification } = useNotificationStore()
  const canEdit = can('attendance:edit')

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<AttendanceRule | null>(null)
  const [form, setForm] = useState<RuleForm>(EMPTY)

  const { data: rules, isLoading, isError } = useQuery({
    queryKey: ['attendance-rules'],
    queryFn:  () => hrApi.attendance.getRules(),
  })

  const toastOk  = (title: string, message?: string) => addNotification({ type: 'success', title, message })
  const toastErr = (e: any) => addNotification({ type: 'error', title: 'Erreur', message: e?.response?.data?.message ?? 'Une erreur est survenue' })
  const refresh  = () => qc.invalidateQueries({ queryKey: ['attendance-rules'] })

  const saveM = useMutation({
    mutationFn: () => editing
      ? hrApi.attendance.updateRule(editing.id, form as any)
      : hrApi.attendance.createRule(form as any),
    onSuccess: () => { toastOk(editing ? 'Règle modifiée' : 'Règle créée'); setModalOpen(false); refresh() },
    onError: toastErr,
  })
  const deleteM = useMutation({
    mutationFn: (id: string) => hrApi.attendance.deleteRule(id),
    onSuccess: () => { toastOk('Règle supprimée'); refresh() },
    onError: toastErr,   // le backend refuse si des employés y sont rattachés (message affiché)
  })

  const openCreate = () => { setEditing(null); setForm(EMPTY); setModalOpen(true) }
  const openEdit = (r: AttendanceRule) => {
    setEditing(r)
    setForm({
      name: r.name, workStartTime: r.workStartTime, workEndTime: r.workEndTime,
      lateThreshold: r.lateThreshold, lunchBreakMinutes: r.lunchBreakMinutes,
      weeklyHours: Number(r.weeklyHours), overtimeThreshold: Number(r.overtimeThreshold),
      debounceMinutes: r.debounceMinutes, isDefault: r.isDefault,
    })
    setModalOpen(true)
  }

  const valid = form.name.trim().length >= 2 && !!form.workStartTime && !!form.workEndTime

  const numField = (label: string, key: keyof RuleForm, hint?: string) => (
    <div>
      <label className="block text-[10px] font-bold tracking-[0.08em] uppercase text-[var(--tx3)] mb-1">{label}</label>
      <input type="number" value={form[key] as number}
        onChange={e => setForm(f => ({ ...f, [key]: Number(e.target.value) }))}
        className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:border-[var(--blue)]" />
      {hint && <p className="text-[11px] text-[var(--tx3)] mt-1">{hint}</p>}
    </div>
  )

  return (
    <div className="space-y-5 nx-fade-up">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Clock className="w-6 h-6 text-[var(--brand-dark)]" />
          <div>
            <h1 className="text-2xl font-bold text-[var(--brand-dark)]">Règles de pointage</h1>
            <p className="text-sm text-[var(--tx3)] mt-0.5">Horaires par site ou équipe (bureau, restaurant…)</p>
          </div>
        </div>
        {canEdit && (
          <button onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium transition-colors">
            <Plus className="w-4 h-4" /> Nouvelle règle
          </button>
        )}
      </div>

      <div className="bg-[var(--bg)] border border-[var(--bd)] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12"><Spinner /></div>
        ) : isError ? (
          <div className="py-12 text-center text-[var(--tx3)] text-sm">Impossible de charger les règles.</div>
        ) : !rules?.length ? (
          <div className="py-12 text-center">
            <Clock className="w-8 h-8 text-[var(--tx3)] mx-auto mb-3" />
            <p className="text-[var(--tx3)] text-sm">Aucune règle. Créez-en une pour définir les horaires.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--bd)]">
                {['Nom', 'Horaires', 'Tolérance retard', 'Pause déj.', 'Défaut', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-[var(--tx3)] uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--bd)]">
              {rules.map(r => (
                <tr key={r.id} className="hover:bg-[var(--surface-2)] transition-colors">
                  <td className="px-4 py-3 font-medium text-[var(--brand-dark)]">{r.name}</td>
                  <td className="px-4 py-3 text-[var(--tx2)]">{r.workStartTime} – {r.workEndTime}</td>
                  <td className="px-4 py-3 text-[var(--tx2)]">{r.lateThreshold} min</td>
                  <td className="px-4 py-3 text-[var(--tx2)]">{r.lunchBreakMinutes} min</td>
                  <td className="px-4 py-3">
                    {r.isDefault && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-[#7C3AED] bg-[#EDE9FE] px-2 py-0.5 rounded-full">
                        <Star className="w-3 h-3" /> Par défaut
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {canEdit && (
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(r)} title="Modifier"
                          className="p-1.5 rounded-lg text-[var(--tx3)] hover:text-[var(--brand-dark)] hover:bg-white border border-[var(--bd)] transition-colors">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          disabled={deleteM.isPending}
                          onClick={() => { if (window.confirm(`Supprimer la règle « ${r.name} » ?`)) deleteM.mutate(r.id) }}
                          title="Supprimer"
                          className="p-1.5 rounded-lg text-[var(--tx3)] hover:text-[#DC2626] hover:bg-white border border-[var(--bd)] transition-colors disabled:opacity-50">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modale création / édition */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setModalOpen(false)}>
          <div className="bg-white rounded-xl p-5 w-full max-w-lg space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-[var(--brand-dark)]">{editing ? 'Modifier la règle' : 'Nouvelle règle'}</h3>
              <button onClick={() => setModalOpen(false)} className="text-[var(--tx3)] hover:text-[var(--brand-dark)]"><X className="w-4 h-4" /></button>
            </div>

            <div>
              <label className="block text-[10px] font-bold tracking-[0.08em] uppercase text-[var(--tx3)] mb-1">Nom</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Ex. Restaurant, Bureau, Équipe du soir"
                className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:border-[var(--blue)]" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-bold tracking-[0.08em] uppercase text-[var(--tx3)] mb-1">Heure de début</label>
                <input type="time" value={form.workStartTime} onChange={e => setForm(f => ({ ...f, workStartTime: e.target.value }))}
                  className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:border-[var(--blue)]" />
              </div>
              <div>
                <label className="block text-[10px] font-bold tracking-[0.08em] uppercase text-[var(--tx3)] mb-1">Heure de fin</label>
                <input type="time" value={form.workEndTime} onChange={e => setForm(f => ({ ...f, workEndTime: e.target.value }))}
                  className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:border-[var(--blue)]" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {numField('Tolérance retard (min)', 'lateThreshold', 'Au-delà, l\'employé est marqué en retard.')}
              {numField('Pause déjeuner (min)', 'lunchBreakMinutes')}
              {numField('Heures/semaine', 'weeklyHours')}
              {numField('Seuil heures sup (h/jour)', 'overtimeThreshold')}
              {numField('Anti-rebond badge (min)', 'debounceMinutes', 'Ignore les rafales de pointages rapprochés.')}
            </div>

            <label className="flex items-center gap-2 text-sm text-[var(--brand-dark)]">
              <input type="checkbox" checked={form.isDefault} onChange={e => setForm(f => ({ ...f, isDefault: e.target.checked }))} />
              Règle par défaut de l'entité (appliquée aux employés sans règle assignée)
            </label>

            <div className="flex justify-end gap-2">
              <button onClick={() => setModalOpen(false)} className="px-3 py-2 rounded-lg text-sm text-[var(--tx2)] hover:bg-[var(--surface-2)]">Annuler</button>
              <button disabled={!valid || saveM.isPending} onClick={() => saveM.mutate()}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white transition-colors disabled:opacity-50">
                {saveM.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editing ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
